import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Factory, Truck, Hammer, ShieldCheck, MapPin } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { useLang } from "@/contexts/LanguageContext";
import { getCollections, staticImg } from "@/lib/api";
import {
  HERO_IMG_INITIAL,
  HERO_IMG_ANIMATE,
  HERO_IMG_TRANSITION,
  SCROLL_HINT_INITIAL,
  SCROLL_HINT_ANIMATE,
  SCROLL_HINT_TRANSITION,
} from "@/lib/motion";

export default function HomePage() {
  const { t, lang } = useLang();
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    getCollections().then(setCollections).catch(() => setCollections([]));
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title={
          lang === "fr"
            ? "ARVÈN Cuisines — Cuisines sur-mesure direct usine | Suisse Romande & France"
            : "ARVÈN Cuisines — Custom kitchens direct from factory | Switzerland & France"
        }
        description={
          lang === "fr"
            ? "ARVÈN Cuisines : fabricant et distributeur direct d'usine de cuisines sur-mesure premium en Suisse Romande (Genève, Lausanne, Yverdon, Fribourg, Neuchâtel, Montreux, Nendaz, Crans-Montana) et France. Importateur direct de Roumanie, normes européennes, sans intermédiaire."
            : "ARVÈN Cuisines: factory-direct manufacturer of premium custom kitchens. Direct importer from Romania, European standards, no middleman. Installation and trades coordination included."
        }
        canonical="https://www.arven-cuisines.com/"
      />
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] w-full overflow-hidden hero-vignette" data-testid="home-hero">
        <motion.img
          initial={HERO_IMG_INITIAL}
          animate={HERO_IMG_ANIMATE}
          transition={HERO_IMG_TRANSITION}
          src={staticImg("/api/static/generated/hero_home.png")}
          alt="ARVEN Hero Kitchen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <Container className="relative h-full flex flex-col justify-end pb-20 md:pb-28">
          <Reveal delay={0.3}>
            <Eyebrow className="mb-6">ARVÈN — CUISINES</Eyebrow>
          </Reveal>
          <Reveal delay={0.5}>
            <h1 className="serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-arven-offwhite max-w-4xl whitespace-pre-line">
              {t.home.hero_h1}
            </h1>
          </Reveal>
          <Reveal delay={0.75}>
            <p className="mt-7 max-w-xl text-base md:text-[17px] leading-relaxed text-arven-offwhite/80">
              {t.home.hero_sub}
            </p>
          </Reveal>
          <Reveal delay={0.95}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/collections" className="btn-gold" data-testid="hero-cta-collections">
                {t.home.hero_cta1} <ArrowRight size={14} />
              </Link>
              <Link to="/rendez-vous" className="btn-outline-gold" data-testid="hero-cta-appointment">
                {t.home.hero_cta2}
              </Link>
            </div>
          </Reveal>
          <motion.div
            initial={SCROLL_HINT_INITIAL}
            animate={SCROLL_HINT_ANIMATE}
            transition={SCROLL_HINT_TRANSITION}
            className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center text-arven-offwhite/60"
          >
            <ChevronDown size={20} />
          </motion.div>
        </Container>
      </section>

      {/* POSITIONING */}
      <Section testid="home-positioning" className="bg-arven-noir">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
            <div className="md:col-span-4">
              <Reveal>
                <Eyebrow>{t.home.positioning_eyebrow}</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-[44px] leading-[1.1] mt-6 whitespace-pre-line">
                  {t.home.positioning_h2}
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-7 md:col-start-6 space-y-5">
              <Reveal delay={0.2}>
                <p className="text-base md:text-[17px] leading-[1.8] text-arven-offwhite/75">
                  {t.home.positioning_p}
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="text-base md:text-[17px] leading-[1.8] text-arven-offwhite/75">
                  {t.home.positioning_p2}
                </p>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* 3 PILLARS */}
      <Section testid="home-pillars" className="bg-arven-noir border-t border-white/5">
        <Container>
          <Reveal>
            <Eyebrow>{t.home.pillars_eyebrow}</Eyebrow>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {[1, 2, 3].map((n, i) => (
              <Reveal key={n} delay={0.15 * i}>
                <div className="group relative">
                  <div className="flex items-baseline gap-4 mb-5">
                    <span className="serif text-arven-gold text-3xl">0{n}</span>
                    <span className="hairline flex-1 max-w-[80px]" />
                  </div>
                  <h3 className="serif text-2xl md:text-[28px] leading-tight">
                    {t.home[`pillar${n}_title`]}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-arven-offwhite/65">
                    {t.home[`pillar${n}_desc`]}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* COLLECTIONS PREVIEW */}
      <Section testid="home-collections" className="bg-arven-surface">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
            <div>
              <Reveal><Eyebrow>{t.home.collections_eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] mt-6 whitespace-pre-line">
                  {t.home.collections_h2}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/collections" className="btn-text-gold">
                {lang === "fr" ? "Voir toutes les collections" : "View all collections"} <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {collections.slice(0, 4).map((c, i) => (
              <Reveal key={c.id} delay={0.1 * i}>
                <Link
                  to={`/collections#${c.slug}`}
                  className="group relative block overflow-hidden rounded-2xl kbd-card transition-all duration-500"
                  data-testid={`home-collection-${c.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={staticImg(c.image)}
                      alt={lang === "fr" ? c.name : c.name_en}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-arven group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="p-7 md:p-8">
                    <div className="flex items-baseline justify-between">
                      <h3 className="serif text-2xl md:text-3xl">
                        {lang === "fr" ? c.name : c.name_en}
                      </h3>
                      <ArrowRight
                        size={18}
                        className="text-arven-offwhite/40 group-hover:text-arven-gold group-hover:translate-x-1 transition-all duration-400"
                      />
                    </div>
                    <p className="mt-3 text-sm text-arven-offwhite/65">
                      {lang === "fr" ? c.tagline : c.tagline_en}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* B2B */}
      <Section testid="home-b2b" className="bg-arven-noir">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="md:col-span-7 order-2 md:order-1">
              <Reveal><Eyebrow>{t.home.b2b_eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-[44px] mt-6 leading-[1.1]">
                  {t.home.b2b_h2}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-[16px] leading-relaxed text-arven-offwhite/70 max-w-xl">
                  {t.home.b2b_p}
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <Link to="/professionnels" className="btn-outline-gold mt-9" data-testid="home-b2b-cta">
                  {t.common.partner} <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="md:col-span-5 order-1 md:order-2">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                <img
                  src={staticImg("/api/static/generated/b2b_showroom.png")}
                  onError={(e) => { e.currentTarget.src = staticImg("/api/static/generated/collection_obscura.png"); }}
                  alt="B2B Showroom"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* REASSURANCE */}
      <Section testid="home-reassurance" className="bg-arven-surface border-y border-white/5">
        <Container>
          <Reveal><Eyebrow>{t.home.reassurance_eyebrow}</Eyebrow></Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {t.home.reassurance.map((r, i) => (
              <Reveal key={r} delay={0.05 * i}>
                <div className="p-5 md:p-6 border border-white/10 rounded-lg text-center text-[12px] md:text-[13px] tracking-wider uppercase text-arven-offwhite/80">
                  {r}
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* MODÈLE DIRECT USINE */}
      <Section testid="home-direct" className="bg-arven-noir">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Reveal><Eyebrow>{t.home.direct_eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-[44px] leading-[1.1] mt-6 whitespace-pre-line">
                  {t.home.direct_h2}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-[15px] md:text-base leading-[1.85] text-arven-offwhite/75">
                  {t.home.direct_p}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {[
                  { icon: Factory, ...t.home.direct_pillars[0] },
                  { icon: Truck, ...t.home.direct_pillars[1] },
                  { icon: ShieldCheck, ...t.home.direct_pillars[2] },
                  { icon: Hammer, ...t.home.direct_pillars[3] },
                ].map(({ icon: Icon, t: title, d }, i) => (
                  <Reveal key={title} delay={0.08 * i}>
                    <div className="p-6 md:p-7 kbd-card rounded-2xl h-full">
                      <Icon size={22} className="text-arven-gold" />
                      <h3 className="serif text-xl md:text-2xl mt-4">{title}</h3>
                      <p className="mt-3 text-[14px] leading-relaxed text-arven-offwhite/65">{d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* ZONES D'INTERVENTION */}
      <Section testid="home-zones" className="bg-arven-surface border-y border-white/5">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
            <div className="lg:col-span-5">
              <Reveal><Eyebrow>{t.home.zones_eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-[40px] leading-tight mt-6 whitespace-pre-line">
                  {t.home.zones_h2}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-[15px] leading-relaxed text-arven-offwhite/70 max-w-md">
                  {t.home.zones_p}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.2}>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {t.home.zones_cities.map((city, i) => (
                    <div
                      key={city}
                      className="flex items-center gap-2.5 p-4 border border-white/10 rounded-xl bg-arven-noir/40"
                      data-testid={`zone-${city.toLowerCase().replace(/\s|-/g, "")}`}
                    >
                      <MapPin size={14} className="text-arven-gold shrink-0" />
                      <span className="text-[13px] md:text-sm text-arven-offwhite/85">{city}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.35}>
                <p className="mt-6 text-[13px] tracking-[0.18em] uppercase text-arven-offwhite/55">
                  {t.home.zones_france}
                </p>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* FINAL CTA */}
      <Section testid="home-final-cta" className="bg-arven-anthracite relative overflow-hidden noise-overlay">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-10 md:py-16">
            <Reveal>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-tight">
                {t.home.cta_h2}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {t.home.cta_p}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 inline-flex gap-4 flex-wrap justify-center">
                <Link to="/rendez-vous" className="btn-gold" data-testid="home-cta-appointment">
                  {t.common.appointment} <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="btn-outline-gold" data-testid="home-cta-contact">
                  {t.common.contact}
                </Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
